/**
 * Aural Warning Splitter
 *
 * Post-processes AssemblyAI utterances to extract aural warnings
 * (GPWS, TCAS, etc.) that were incorrectly attributed to human speakers.
 *
 * AssemblyAI's diarization often merges aural warnings into the nearest
 * human speaker's utterance. This utility splits those utterances and
 * re-attributes the warning portions to "Voice Alert".
 */

// ── Aural Warning Patterns ──────────────────────────────────────────────────
// Each pattern matches a complete aural warning phrase (case-insensitive).
// Ordered longest-first so longer phrases match before shorter subsets.
// NOTE: Airbus "RETARD" auto-callout is intentionally excluded — too many
// false positives in normal speech. Revisit with position-aware matching.
const AURAL_WARNING_PHRASES = [
  // ─── GPWS / EGPWS (Honeywell MK V/VI/VII/VIII — universal) ────────────────
  'terrain ahead pull up',
  'too low terrain',
  'too low gear',
  'too low flaps',
  'caution terrain',
  'terrain terrain',
  'terrain ahead',
  // bare 'terrain' removed — matches crew briefings ("terrain avoidance", "terrain ahead"
  // already handled by the multi-word variants above).
  'dont sink',
  "don't sink",
  'wind shear',
  'windshear',
  'pull up',
  'sink rate',
  'bank angle',
  'glideslope',
  'obstacle obstacle',

  // ─── TCAS II v7.1 (Honeywell/Collins — universal) ─────────────────────────
  'maintain vertical speed crossing maintain',
  'monitor vertical speed',
  'adjust vertical speed',
  'maintain vertical speed',
  'traffic traffic',
  'climb climb',
  'descend descend',
  'increase climb',
  'increase descent',
  'crossing climb',
  'crossing descend',
  'level off level off',
  'clear of conflict',

  // ─── Airbus-specific ──────────────────────────────────────────────────────
  // NOTE: bare 'minimum'/'minimums' removed — crew uses these as routine checklist
  // terms ("set the minimums to 500"). Only the Boeing 'approaching minimums' callout
  // is a reliable synthetic-voice phrase.
  'hundred above',

  // ─── Boeing-specific ──────────────────────────────────────────────────────
  'approaching minimums',

  // ─── Stall / Cabin / Config (universal) ───────────────────────────────────
  // NOTE: bare 'config' removed — "config check" and "configuration" appear
  // in every pre-flight checklist.
  'stall stall',
  'cabin altitude',
] as const;

/**
 * Build a regex that matches one or more consecutive aural warning phrases,
 * optionally separated by commas, "and", or whitespace.
 * E.g. "Wind shear, wind shear, wind shear" or "Don't sink, don't sink"
 */
function buildWarningRegex(): RegExp {
  // Escape special regex chars in phrases
  const escaped = AURAL_WARNING_PHRASES.map((p) =>
    p.replace(/[-.*+?^${}()|\\[\]]/g, '\\$&').replace(/'/g, "['\u2019]?")
  );
  // Match a single warning phrase, optionally followed by punctuation
  const singlePhrase = `(?:${escaped.join('|')})[.!,]*`;
  // Match one or more repetitions separated by whitespace (commas already consumed above)
  const repeated = `${singlePhrase}(?:\\s+(?:and\\s+)?${singlePhrase})*`;
  return new RegExp(`(${repeated})`, 'gi');
}

const WARNING_REGEX = buildWarningRegex();

/** A generic utterance with the fields we need to split */
export interface SplittableUtterance {
  speaker: string;
  text: string;
  start: number;
  end: number;
  confidence: number;
  /** Any extra fields are preserved */
  [key: string]: unknown;
}

/**
 * Check if a speaker label is already a non-human / voice alert label.
 * We don't need to split utterances from speakers already tagged as Voice Alert.
 */
function isVoiceAlertSpeaker(speaker: string): boolean {
  const s = speaker.toLowerCase();
  return s.includes('voice alert') || s.includes('aural warning');
}

/**
 * Split an utterance into human speech and aural warning segments.
 *
 * Given an utterance like:
 *   "Wind shear, wind shear, wind shear. Recover. Okay, stay on wing level."
 * Returns:
 *   [
 *     { speaker: "Voice Alert", text: "Wind shear, wind shear, wind shear.", ... },
 *     { speaker: "First Officer", text: "Recover. Okay, stay on wing level.", ... },
 *   ]
 *
 * Timestamps are estimated proportionally based on text length.
 */
export function splitAuralWarnings<T extends SplittableUtterance>(
  utterances: T[],
  voiceAlertLabel = 'Voice Alert',
): T[] {
  const result: T[] = [];

  for (const utterance of utterances) {
    // Skip utterances already from Voice Alert
    if (isVoiceAlertSpeaker(utterance.speaker)) {
      result.push(utterance);
      continue;
    }

    // Reset regex lastIndex for each utterance
    WARNING_REGEX.lastIndex = 0;

    const text = utterance.text;
    const segments: Array<{ text: string; isWarning: boolean }> = [];
    let lastIndex = 0;

    let match: RegExpExecArray | null;
    while ((match = WARNING_REGEX.exec(text)) !== null) {
      // Text before the warning
      if (match.index > lastIndex) {
        const before = text.slice(lastIndex, match.index).trim();
        if (before) {
          segments.push({ text: before, isWarning: false });
        }
      }
      // The warning itself
      segments.push({ text: match[0].trim(), isWarning: true });
      lastIndex = match.index + match[0].length;
    }

    // Remaining text after last warning
    if (lastIndex < text.length) {
      const after = text.slice(lastIndex).trim();
      if (after) {
        segments.push({ text: after, isWarning: false });
      }
    }

    // If no warnings found, keep original utterance
    if (segments.length === 0 || !segments.some((s) => s.isWarning)) {
      result.push(utterance);
      continue;
    }

    // Merge consecutive segments with the same type (e.g. 3 separate "wind shear," → one block)
    const merged: Array<{ text: string; isWarning: boolean }> = [];
    for (const seg of segments) {
      const prev = merged[merged.length - 1];
      if (prev && prev.isWarning === seg.isWarning) {
        prev.text = prev.text + ' ' + seg.text;
      } else {
        merged.push({ ...seg });
      }
    }

    // Estimate timestamps proportionally based on character length
    const totalChars = merged.reduce((sum, s) => sum + s.text.length, 0);
    const duration = utterance.end - utterance.start;
    let currentTime = utterance.start;

    for (const segment of merged) {
      const segDuration = totalChars > 0
        ? (segment.text.length / totalChars) * duration
        : duration / merged.length;

      const segEnd = currentTime + segDuration;

      result.push({
        ...utterance,
        speaker: segment.isWarning ? voiceAlertLabel : utterance.speaker,
        text: segment.text,
        start: Math.round(currentTime),
        end: Math.round(segEnd),
      } as T);

      currentTime = segEnd;
    }
  }

  return result;
}